import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { DashboardApi, DashboardConfig } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import './AttributeEditor.css';
import './FormsPage.css';

const FONT_FAMILY_OPTIONS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: '"Helvetica Neue", Arial, sans-serif', label: 'Helvetica Neue' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: '"Courier New", monospace', label: 'Courier New' },
];

const FIELD_TYPE_LABELS = {
  string: 'Kort tekst',
  text: 'Lang tekst',
  integer: 'Tall',
  boolean: 'Ja/nei',
  select: 'Valgliste',
  product: 'Produkt valg',
  date: 'Dato',
  datetime: 'Dato og tid',
  location: 'Lokasjon',
};

const CLOSED_ADVANCED_SECTIONS = {
  design: false,
  install: false,
};

const TRANSPARENT_FORM_BACKGROUND = 'transparent';
const FORM_BACKGROUND_FALLBACK = '#ffffff';
const FORM_THEME_COLOR_FIELDS = [
  ['labelText', 'Etiketttekst'],
  ['helpText', 'Hjelpetekst'],
  ['inputText', 'Input-tekst'],
  ['inputBackground', 'Input-bakgrunn'],
  ['inputBorder', 'Input-border'],
  ['buttonBackground', 'Knapp-bakgrunn'],
  ['buttonText', 'Knapp-tekst'],
];

const createDefaultThemeConfig = () => ({
  typography: {
    fontFamily: 'Arial, sans-serif',
    textSize: 16,
  },
  colors: {
    formBackground: '#ffffff',
    labelText: '#111827',
    helpText: '#6b7280',
    inputText: '#111827',
    inputBackground: '#ffffff',
    inputBorder: '#d1d5db',
    buttonBackground: '#111827',
    buttonText: '#ffffff',
    successBackground: '#ecfdf5',
    successText: '#065f46',
  },
  borders: {
    inputBorderWidth: 1,
    inputBorderRadius: 12,
  },
  spacing: {
    fieldGap: 16,
    formPadding: 20,
  },
  buttonText: 'Send inn',
});

const mergeThemeConfig = (themeConfig = {}) => {
  const defaults = createDefaultThemeConfig();
  return {
    typography: {
      ...defaults.typography,
      ...(themeConfig.typography || {}),
    },
    colors: {
      ...defaults.colors,
      ...(themeConfig.colors || {}),
    },
    borders: {
      ...defaults.borders,
      ...(themeConfig.borders || {}),
    },
    spacing: {
      ...defaults.spacing,
      ...(themeConfig.spacing || {}),
    },
    buttonText: themeConfig.buttonText || defaults.buttonText,
  };
};

const createEmptyDraft = () => ({
  id: null,
  externalId: null,
  name: '',
  botId: '',
  inquiryTypeId: '',
  botProductId: '',
  status: 'active',
  fieldConfig: [],
  themeConfig: createDefaultThemeConfig(),
  validation: null,
});

const mapFormItem = (item) => ({
  id: item?.id ?? null,
  externalId: item?.external_id || item?.externalId || null,
  name: item?.name || '',
  botId: item?.bot_id ?? item?.botId ?? '',
  inquiryTypeId: item?.inquiry_type_id ?? item?.inquiryTypeId ?? '',
  botProductId: item?.bot_product_id ?? item?.botProductId ?? '',
  status: item?.status || 'active',
  botName: item?.bot_name || item?.botName || '',
  inquiryTypeName: item?.inquiry_type_name || item?.inquiryTypeName || '',
  botProductName: item?.bot_product_name || item?.botProductName || '',
  fieldConfig: Array.isArray(item?.field_config)
    ? item.field_config.map((field) => ({
      inquiryAttributeKey: field?.inquiryAttributeKey || field?.inquiry_attribute_key || '',
      required: field?.required === true,
      order: Number.isFinite(Number(field?.order)) ? Number(field.order) : 0,
      labelOverride: field?.labelOverride || field?.label_override || '',
      placeholder: field?.placeholder || '',
      helpText: field?.helpText || field?.help_text || '',
    }))
    : [],
  themeConfig: mergeThemeConfig(item?.theme_config || item?.themeConfig || {}),
  validation: item?.validation || null,
});

const createFieldConfigFromAttribute = (attribute, index) => ({
  inquiryAttributeKey: attribute?.key || '',
  required: attribute?.required === true,
  order: index,
  labelOverride: '',
  placeholder: attribute?.placeholder || '',
  helpText: attribute?.helpText || attribute?.help_text || '',
});

const buildFieldConfigPayload = (draft) => (
  (draft.fieldConfig || []).map((field, index) => ({
    inquiryAttributeKey: field.inquiryAttributeKey,
    required: field.required === true,
    order: index,
    labelOverride: String(field.labelOverride || '').trim() || null,
    placeholder: String(field.placeholder || '').trim() || null,
    helpText: String(field.helpText || '').trim() || null,
  }))
);

const normalizeInquiryTypeItem = (item) => ({
  id: item?.id ?? null,
  name: item?.name || '',
  status: item?.status || 'active',
  description: item?.description || '',
  attributes: Array.isArray(item?.attributes) ? item.attributes : [],
});

const moveItem = (items, index, direction) => {
  const next = [...items];
  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= next.length) return next;
  const [entry] = next.splice(index, 1);
  next.splice(targetIndex, 0, entry);
  return next.map((item, orderIndex) => ({ ...item, order: orderIndex }));
};

const createDraftFromForm = (form) => ({
  ...createEmptyDraft(),
  ...form,
  botId: String(form?.botId || ''),
  inquiryTypeId: String(form?.inquiryTypeId || ''),
  botProductId: form?.botProductId ? String(form.botProductId) : '',
  fieldConfig: Array.isArray(form?.fieldConfig) ? form.fieldConfig.map((field) => ({ ...field })) : [],
  themeConfig: mergeThemeConfig(form?.themeConfig || {}),
});

const isTransparentFormBackground = (value) =>
  typeof value === 'string' && value.trim().toLowerCase() === TRANSPARENT_FORM_BACKGROUND;

const getColorPickerValue = (value, fallback) =>
  isTransparentFormBackground(value) ? fallback : value;

export default function FormsPage() {
  const { selectedCompanyId, permissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState([]);
  const [bots, setBots] = useState([]);
  const [filters, setFilters] = useState({ botId: 'all', inquiryTypeId: 'all' });
  const [draft, setDraft] = useState(createEmptyDraft());
  const [activeEditorKey, setActiveEditorKey] = useState(null);
  const [inquiryTypesByBot, setInquiryTypesByBot] = useState({});
  const [productsByBot, setProductsByBot] = useState({});
  const [openAdvancedSections, setOpenAdvancedSections] = useState(CLOSED_ADVANCED_SECTIONS);

  const canView = Boolean(permissions?.canViewForms || permissions?.canViewBots);
  const canEdit = Boolean(permissions?.canManageForms || permissions?.canEditBots);

  const resetAdvancedSections = useCallback(() => {
    setOpenAdvancedSections(CLOSED_ADVANCED_SECTIONS);
  }, []);

  const closeEditor = useCallback(() => {
    setActiveEditorKey(null);
    setDraft(createEmptyDraft());
    resetAdvancedSections();
  }, [resetAdvancedSections]);

  const loadInquiryTypes = useCallback(async (botId, { force = false } = {}) => {
    if (!botId) return [];
    if (!force && inquiryTypesByBot[botId]) {
      return inquiryTypesByBot[botId];
    }
    const response = await DashboardApi.getBotInquiryTypes(botId, { includeArchived: false });
    const items = Array.isArray(response?.items) ? response.items.map(normalizeInquiryTypeItem) : [];
    setInquiryTypesByBot((prev) => ({ ...prev, [botId]: items }));
    return items;
  }, [inquiryTypesByBot]);

  const loadProducts = useCallback(async (botId, { force = false } = {}) => {
    if (!botId || !selectedCompanyId) return [];
    if (!force && productsByBot[botId]) {
      return productsByBot[botId];
    }
    const response = await DashboardApi.getCompanyProducts(selectedCompanyId, { includeArchived: false });
    const rawItems = Array.isArray(response?.items)
      ? response.items
      : Array.isArray(response?.products)
        ? response.products
        : [];
    const items = rawItems
      .filter((item) => {
        const assignedBotIds = Array.isArray(item?.assigned_bot_ids ?? item?.assignedBotIds)
          ? (item.assigned_bot_ids ?? item.assignedBotIds).map((id) => String(id))
          : [];
        return assignedBotIds.includes(String(botId));
      })
      .map((item) => ({
        id: item?.id ?? item?.bot_product_id ?? null,
        name: item?.name || item?.product_name || '',
        status: item?.status || 'active',
        colorCode: item?.color_code || item?.colorCode || null,
      }))
      .filter((item) => item.id && item.status !== 'archived');
    setProductsByBot((prev) => ({ ...prev, [botId]: items }));
    return items;
  }, [productsByBot, selectedCompanyId]);

  const loadPageData = useCallback(async () => {
    if (!selectedCompanyId) {
      setForms([]);
      setBots([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [botsResponse, formsResponse] = await Promise.all([
        DashboardApi.getBots(null, selectedCompanyId),
        DashboardApi.getForms(selectedCompanyId, { includeArchived: true }),
      ]);
      const nextBots = Array.isArray(botsResponse?.bots)
        ? botsResponse.bots
        : Array.isArray(botsResponse)
          ? botsResponse
          : [];
      const nextForms = Array.isArray(formsResponse?.items)
        ? formsResponse.items.map(mapFormItem)
        : [];
      setBots(nextBots);
      setForms(nextForms);
    } catch (error) {
      notifyError(error.message || 'Kunne ikke laste skjemaer.', { source: 'Skjema' });
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  useEffect(() => {
    closeEditor();
  }, [selectedCompanyId, closeEditor]);

  useEffect(() => {
    if (!draft.botId) {
      return;
    }
    Promise.all([
      loadInquiryTypes(draft.botId),
      loadProducts(draft.botId),
    ]).catch((error) => {
      notifyError(error.message || 'Kunne ikke laste skjemadata.', { source: 'Skjema' });
    });
  }, [draft.botId, loadInquiryTypes, loadProducts]);

  const availableInquiryTypes = useMemo(
    () => inquiryTypesByBot[draft.botId] || [],
    [draft.botId, inquiryTypesByBot],
  );

  const selectedInquiryType = useMemo(
    () => availableInquiryTypes.find((item) => String(item.id) === String(draft.inquiryTypeId)) || null,
    [availableInquiryTypes, draft.inquiryTypeId],
  );

  const filteredForms = useMemo(() => (
    forms.filter((form) => {
      if (filters.botId !== 'all' && String(form.botId) !== String(filters.botId)) return false;
      if (filters.inquiryTypeId !== 'all' && String(form.inquiryTypeId) !== String(filters.inquiryTypeId)) return false;
      return true;
    })
  ), [filters, forms]);

  const openEditorForForm = useCallback(async (form) => {
    const nextDraft = createDraftFromForm(form);
    setDraft(nextDraft);
    setActiveEditorKey(form?.externalId || null);
    resetAdvancedSections();
    if (nextDraft.botId) {
      try {
        await Promise.all([
          loadInquiryTypes(nextDraft.botId),
          loadProducts(nextDraft.botId),
        ]);
      } catch (error) {
        notifyError(error.message || 'Kunne ikke laste skjemadata.', { source: 'Skjema' });
      }
    }
  }, [loadInquiryTypes, loadProducts, resetAdvancedSections]);

  const handleCreateNew = useCallback(() => {
    setDraft(createEmptyDraft());
    setActiveEditorKey('new');
    resetAdvancedSections();
  }, [resetAdvancedSections]);

  const handleToggleExisting = useCallback(async (form) => {
    if (!form?.externalId) return;
    if (activeEditorKey === form.externalId) {
      closeEditor();
      return;
    }
    await openEditorForForm(form);
  }, [activeEditorKey, closeEditor, openEditorForForm]);

  const handleDraftChange = useCallback((field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleThemeChange = useCallback((section, field, value) => {
    setDraft((prev) => ({
      ...prev,
      themeConfig: {
        ...prev.themeConfig,
        [section]: {
          ...prev.themeConfig[section],
          [field]: value,
        },
      },
    }));
  }, []);

  const handleTopThemeChange = useCallback((field, value) => {
    setDraft((prev) => ({
      ...prev,
      themeConfig: {
        ...prev.themeConfig,
        [field]: value,
      },
    }));
  }, []);

  const handleFormBackgroundTransparencyToggle = useCallback((enabled) => {
    setDraft((prev) => ({
      ...prev,
      themeConfig: {
        ...prev.themeConfig,
        colors: {
          ...prev.themeConfig.colors,
          formBackground: enabled
            ? TRANSPARENT_FORM_BACKGROUND
            : createDefaultThemeConfig().colors.formBackground,
        },
      },
    }));
  }, []);

  const handleBotChange = useCallback(async (botId) => {
    setDraft((prev) => ({
      ...prev,
      botId,
      inquiryTypeId: '',
      botProductId: '',
      fieldConfig: [],
    }));
    if (!botId) return;
    try {
      await Promise.all([
        loadInquiryTypes(botId, { force: true }),
        loadProducts(botId, { force: true }),
      ]);
    } catch (error) {
      notifyError(error.message || 'Kunne ikke laste skjemadata.', { source: 'Skjema' });
    }
  }, [loadInquiryTypes, loadProducts]);

  const handleInquiryTypeChange = useCallback((inquiryTypeId) => {
    const nextInquiryType = (inquiryTypesByBot[draft.botId] || []).find(
      (item) => String(item.id) === String(inquiryTypeId),
    );
    setDraft((prev) => ({
      ...prev,
      inquiryTypeId,
      botProductId: '',
      fieldConfig: Array.isArray(nextInquiryType?.attributes)
        ? nextInquiryType.attributes.map((attribute, index) => createFieldConfigFromAttribute(attribute, index))
        : [],
    }));
  }, [draft.botId, inquiryTypesByBot]);

  const handleFieldIncludedChange = useCallback((attribute, checked) => {
    setDraft((prev) => {
      const existingIndex = (prev.fieldConfig || []).findIndex(
        (field) => field.inquiryAttributeKey === attribute.key,
      );
      if (checked && existingIndex === -1) {
        return {
          ...prev,
          fieldConfig: [
            ...(prev.fieldConfig || []),
            createFieldConfigFromAttribute(attribute, (prev.fieldConfig || []).length),
          ],
        };
      }
      if (!checked && existingIndex >= 0) {
        return {
          ...prev,
          fieldConfig: prev.fieldConfig
            .filter((field) => field.inquiryAttributeKey !== attribute.key)
            .map((field, index) => ({ ...field, order: index })),
        };
      }
      return prev;
    });
  }, []);

  const selectedFormHasProductField = useMemo(() => {
    if (!selectedInquiryType) return false;
    const selectedKeys = new Set((draft.fieldConfig || []).map((field) => field.inquiryAttributeKey));
    return selectedInquiryType.attributes.some((attribute) => (
      selectedKeys.has(attribute.key)
      && attribute?.type === 'product'
    ));
  }, [draft.fieldConfig, selectedInquiryType]);

  const availableProducts = useMemo(
    () => productsByBot[draft.botId] || [],
    [draft.botId, productsByBot],
  );

  const handleFieldConfigChange = useCallback((fieldKey, prop, value) => {
    setDraft((prev) => ({
      ...prev,
      fieldConfig: (prev.fieldConfig || []).map((field) => (
        field.inquiryAttributeKey === fieldKey
          ? { ...field, [prop]: value }
          : field
      )),
    }));
  }, []);

  const handleFieldMove = useCallback((fieldKey, direction) => {
    setDraft((prev) => {
      const index = (prev.fieldConfig || []).findIndex((field) => field.inquiryAttributeKey === fieldKey);
      if (index === -1) return prev;
      return {
        ...prev,
        fieldConfig: moveItem(prev.fieldConfig || [], index, direction),
      };
    });
  }, []);

  const handleCopySnippet = useCallback(async () => {
    if (!draft.externalId) return;
    try {
      await navigator.clipboard.writeText(DashboardConfig.getFormEmbedSnippet(draft.externalId));
      notifySuccess('Skjema-koden ble kopiert.', { source: 'Skjema' });
    } catch (error) {
      notifyError(error.message || 'Kunne ikke kopiere koden.', { source: 'Skjema' });
    }
  }, [draft.externalId]);

  const handleSave = useCallback(async () => {
    if (!selectedCompanyId) return;
    if (!draft.name.trim()) {
      notifyError('Gi skjemaet et navn.', { source: 'Skjema' });
      return;
    }
    if (!draft.botId) {
      notifyError('Velg hvilken bot skjemaet skal tilhøre.', { source: 'Skjema' });
      return;
    }
    if (!draft.inquiryTypeId) {
      notifyError('Velg en henvendelsestype.', { source: 'Skjema' });
      return;
    }
    if ((draft.fieldConfig || []).length === 0) {
      notifyError('Velg minst ett felt i skjemaet.', { source: 'Skjema' });
      return;
    }

    const payload = {
      name: draft.name.trim(),
      botId: Number(draft.botId),
      inquiryTypeId: Number(draft.inquiryTypeId),
      botProductId: selectedFormHasProductField || !draft.botProductId ? null : Number(draft.botProductId),
      status: draft.status || 'active',
      fieldConfig: buildFieldConfigPayload(draft),
      themeConfig: draft.themeConfig,
    };

    setSaving(true);
    try {
      const response = draft.externalId
        ? await DashboardApi.updateForm(draft.externalId, selectedCompanyId, payload)
        : await DashboardApi.createForm(selectedCompanyId, payload);
      const saved = mapFormItem(response?.item || {});
      setForms((prev) => {
        const existingIndex = prev.findIndex((item) => item.externalId === saved.externalId);
        if (existingIndex === -1) {
          return [saved, ...prev];
        }
        const next = [...prev];
        next[existingIndex] = saved;
        return next;
      });
      await openEditorForForm(saved);
      notifySuccess('Skjemaet ble lagret.', { source: 'Skjema' });
    } catch (error) {
      notifyError(error.message || 'Kunne ikke lagre skjemaet.', { source: 'Skjema' });
    } finally {
      setSaving(false);
    }
  }, [draft, openEditorForForm, selectedCompanyId, selectedFormHasProductField]);

  const handleArchive = useCallback(async (form) => {
    if (!form?.externalId || !selectedCompanyId) return;
    setSaving(true);
    try {
      await DashboardApi.deleteForm(form.externalId, selectedCompanyId);
      setForms((prev) => prev.map((entry) => (
        entry.externalId === form.externalId
          ? { ...entry, status: 'archived' }
          : entry
      )));
      if (activeEditorKey === form.externalId) {
        closeEditor();
      }
      notifySuccess('Skjemaet ble arkivert.', { source: 'Skjema' });
    } catch (error) {
      notifyError(error.message || 'Kunne ikke arkivere skjemaet.', { source: 'Skjema' });
    } finally {
      setSaving(false);
    }
  }, [activeEditorKey, closeEditor, selectedCompanyId]);

  const toggleAdvancedSection = useCallback((section) => {
    setOpenAdvancedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const renderEditor = (mode) => (
    <div className="forms-editor">
      <div className="forms-editor-head">
        <div>
          <h3>{mode === 'new' ? 'Nytt skjema' : 'Rediger skjema'}</h3>
          <p className="input-hint">
            Skjemaet bruker feltene fra valgt henvendelsestype og lagrer verdiene rett inn i inboxen.
          </p>
        </div>
        <div className="forms-editor-head-actions">
          <button type="button" className="button button-secondary" onClick={closeEditor} disabled={saving}>
            Lukk
          </button>
          {draft.externalId ? (
            <>
              <button type="button" className="button button-secondary" onClick={handleCopySnippet}>
                Kopier embed-kode
              </button>
              <button
                type="button"
                className="button button-danger"
                onClick={() => handleArchive(draft)}
                disabled={!canEdit || saving}
              >
                Arkiver
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="forms-editor-grid">
        <label>
          Navn
          <input
            className="input"
            type="text"
            value={draft.name}
            onChange={(event) => handleDraftChange('name', event.target.value)}
            disabled={!canEdit || saving}
            placeholder="f.eks. Kontaktskjema for salg"
          />
        </label>
        <label>
          Bot
          <select
            className="select"
            value={draft.botId}
            onChange={(event) => handleBotChange(event.target.value)}
            disabled={!canEdit || saving}
          >
            <option value="">Velg bot</option>
            {bots.map((bot) => (
              <option key={bot.id} value={bot.id}>
                {bot.display_title || bot.name}
              </option>
            ))}
          </select>
        </label>
        <label className="forms-span-all">
          Henvendelsestype
          <select
            className="select"
            value={draft.inquiryTypeId}
            onChange={(event) => handleInquiryTypeChange(event.target.value)}
            disabled={!canEdit || saving || !draft.botId}
          >
            <option value="">Velg henvendelsestype</option>
            {availableInquiryTypes.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="forms-editor-section">
        <div className="forms-section-head">
          <div>
            <h4>Felter</h4>
            <p className="input-hint">Velg hvilke inquiry-felter som skal vises på nettsiden, og tilpass etikett, hjelpetekst og påkrevd-status.</p>
          </div>
        </div>
        {!selectedInquiryType ? (
          <p className="input-hint">Velg en henvendelsestype for å konfigurere feltene.</p>
        ) : (
          <div className="forms-field-layout">
            <div className="forms-field-chooser">
              <h5>Tilgjengelige felter</h5>
              <div className="forms-field-chooser-list">
                {selectedInquiryType.attributes.map((attribute) => {
                  const isIncluded = draft.fieldConfig.some((field) => field.inquiryAttributeKey === attribute.key);
                  return (
                    <button
                      key={attribute.key}
                      type="button"
                      className={`forms-field-pill${isIncluded ? ' is-selected' : ''}`}
                      onClick={() => handleFieldIncludedChange(attribute, !isIncluded)}
                      disabled={!canEdit || saving}
                      aria-pressed={isIncluded}
                      title={`${attribute.label} · ${FIELD_TYPE_LABELS[attribute.type] || attribute.type} · ${attribute.key}`}
                    >
                      <span className="forms-field-pill-label">{attribute.label}</span>
                      <span className={`forms-field-pill-toggle${isIncluded ? ' is-on' : ''}`} aria-hidden="true">
                        <span className="forms-field-pill-toggle-thumb" />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="forms-field-config">
              <h5>Valgte felter</h5>
              {(draft.fieldConfig || []).length === 0 ? (
                <p className="input-hint">Ingen felter valgt ennå.</p>
              ) : (
                <div className="forms-selected-fields">
                  {draft.fieldConfig.map((field, index) => {
                    const attribute = selectedInquiryType.attributes.find((item) => item.key === field.inquiryAttributeKey);
                    if (!attribute) {
                      return (
                        <div key={field.inquiryAttributeKey} className="forms-selected-field is-invalid">
                          <strong>{field.inquiryAttributeKey}</strong>
                          <p>Dette feltet finnes ikke lenger i henvendelsestypen.</p>
                        </div>
                      );
                    }
                    return (
                      <article key={field.inquiryAttributeKey} className="product-inbox-config-row inquiry-attribute-row forms-selected-field">
                        <header className="inquiry-attribute-summary forms-selected-field-head">
                          <div className="inquiry-attribute-summary-main">
                            <div className="inquiry-attribute-title-line">
                              <strong>{attribute.label}</strong>
                            </div>
                            <span>{FIELD_TYPE_LABELS[attribute.type] || attribute.type}</span>
                          </div>
                          <div className="inquiry-attribute-summary-actions forms-selected-field-actions">
                            <button
                              type="button"
                              className="button button-secondary button-small"
                              onClick={() => handleFieldMove(field.inquiryAttributeKey, 'up')}
                              disabled={!canEdit || saving || index === 0}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="button button-secondary button-small"
                              onClick={() => handleFieldMove(field.inquiryAttributeKey, 'down')}
                              disabled={!canEdit || saving || index === draft.fieldConfig.length - 1}
                            >
                              ↓
                            </button>
                          </div>
                        </header>
                        <div className="forms-selected-field-grid inquiry-attribute-fields">
                          <label>
                            Etikett på nettsiden
                            <input
                              className="input"
                              type="text"
                              value={field.labelOverride}
                              onChange={(event) => handleFieldConfigChange(field.inquiryAttributeKey, 'labelOverride', event.target.value)}
                              disabled={!canEdit || saving}
                              placeholder={attribute.label}
                            />
                          </label>
                          <label className="checkbox-field">
                            <input
                              type="checkbox"
                              checked={field.required === true}
                              onChange={(event) => handleFieldConfigChange(field.inquiryAttributeKey, 'required', event.target.checked)}
                              disabled={!canEdit || saving}
                            />
                            <span>Påkrevd i skjemaet</span>
                          </label>
                          <label className="forms-span-all">
                            Placeholder
                            <input
                              className="input"
                              type="text"
                              value={field.placeholder}
                              onChange={(event) => handleFieldConfigChange(field.inquiryAttributeKey, 'placeholder', event.target.value)}
                              disabled={!canEdit || saving}
                              placeholder={attribute.placeholder || ''}
                            />
                          </label>
                          <label className="forms-span-all">
                            Hjelpetekst
                            <input
                              className="input"
                              type="text"
                              value={field.helpText}
                              onChange={(event) => handleFieldConfigChange(field.inquiryAttributeKey, 'helpText', event.target.value)}
                              disabled={!canEdit || saving}
                              placeholder={attribute.helpText || attribute.help_text || ''}
                            />
                          </label>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {selectedInquiryType && !selectedFormHasProductField ? (
        <div className="forms-editor-section forms-product-assignment">
          <div className="forms-section-head">
            <div>
              <h4>Produkt</h4>
              <p className="input-hint">
                Velg et fast produkt for henvendelser fra dette skjemaet.
              </p>
            </div>
          </div>
          <label className="forms-product-select">
            Produkt
            <select
              className="select"
              value={draft.botProductId || ''}
              onChange={(event) => handleDraftChange('botProductId', event.target.value)}
              disabled={!canEdit || saving || !draft.botId || availableProducts.length === 0}
            >
              <option value="">Ingen fast produkt</option>
              {availableProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name || `Produkt ${product.id}`}
                </option>
              ))}
            </select>
            {availableProducts.length === 0 ? (
              <span className="input-hint">Denne boten har ingen aktive produkter.</span>
            ) : null}
          </label>
        </div>
      ) : null}

      <section className={`forms-advanced-card${openAdvancedSections.design ? ' is-open' : ''}`}>
        <button
          type="button"
          className="forms-advanced-toggle"
          onClick={() => toggleAdvancedSection('design')}
          aria-expanded={openAdvancedSections.design}
        >
          <span>
            <strong>Design</strong>
            <small>Kuraterte kontroller for typografi, farger, border og spacing.</small>
          </span>
          <span>{openAdvancedSections.design ? 'Skjul' : 'Vis'}</span>
        </button>
        {openAdvancedSections.design ? (
          <div className="forms-advanced-body">
            <div className="forms-theme-grid">
              <label>
                Font
                <select
                  className="select"
                  value={draft.themeConfig.typography.fontFamily}
                  onChange={(event) => handleThemeChange('typography', 'fontFamily', event.target.value)}
                  disabled={!canEdit || saving}
                >
                  {FONT_FAMILY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Tekststørrelse
                <input
                  className="input"
                  type="number"
                  min="12"
                  max="24"
                  value={draft.themeConfig.typography.textSize}
                  onChange={(event) => handleThemeChange('typography', 'textSize', Number(event.target.value || 16))}
                  disabled={!canEdit || saving}
                />
              </label>
              <div className="forms-theme-color-control">
                <span className="forms-theme-color-title">Bakgrunn</span>
                <div className="forms-theme-background-card">
                  <div className="forms-theme-background-head">
                    <div
                      className={`forms-color-swatch forms-color-swatch-large${isTransparentFormBackground(draft.themeConfig.colors.formBackground) ? ' is-transparent' : ''}`}
                      style={{
                        backgroundColor: getColorPickerValue(
                          draft.themeConfig.colors.formBackground,
                          FORM_BACKGROUND_FALLBACK,
                        ),
                      }}
                    />
                    <div className="forms-theme-background-copy">
                      <strong>{isTransparentFormBackground(draft.themeConfig.colors.formBackground) ? 'Transparent' : 'Fast farge'}</strong>
                      <small>
                        {isTransparentFormBackground(draft.themeConfig.colors.formBackground)
                          ? 'Formkortet får ingen egen bakgrunn.'
                          : 'Velg en egen flate for skjemakortet.'}
                      </small>
                    </div>
                  </div>
                  <div className="forms-theme-background-mode" role="group" aria-label="Bakgrunnsmodus">
                    <button
                      type="button"
                      className={`forms-background-mode-pill${!isTransparentFormBackground(draft.themeConfig.colors.formBackground) ? ' is-active' : ''}`}
                      onClick={() => handleFormBackgroundTransparencyToggle(false)}
                      disabled={!canEdit || saving}
                    >
                      Solid
                    </button>
                    <button
                      type="button"
                      className={`forms-background-mode-pill${isTransparentFormBackground(draft.themeConfig.colors.formBackground) ? ' is-active' : ''}`}
                      onClick={() => handleFormBackgroundTransparencyToggle(true)}
                      disabled={!canEdit || saving}
                    >
                      Transparent
                    </button>
                  </div>
                  <label className="forms-theme-color-picker-row">
                    <span>Farge</span>
                    <div className="forms-theme-color-picker-shell">
                      <input
                        className="forms-color-picker-native"
                        type="color"
                        value={getColorPickerValue(
                          draft.themeConfig.colors.formBackground,
                          FORM_BACKGROUND_FALLBACK,
                        )}
                        onChange={(event) => handleThemeChange('colors', 'formBackground', event.target.value)}
                        disabled={!canEdit || saving || isTransparentFormBackground(draft.themeConfig.colors.formBackground)}
                      />
                      <span className="forms-theme-color-value">
                        {getColorPickerValue(
                          draft.themeConfig.colors.formBackground,
                          FORM_BACKGROUND_FALLBACK,
                        ).toUpperCase()}
                      </span>
                    </div>
                  </label>
                </div>
              </div>
              <div className="forms-theme-color-grid forms-span-all">
                {FORM_THEME_COLOR_FIELDS.map(([field, label]) => (
                  <label key={field} className="forms-theme-color-card">
                    <span className="forms-theme-color-card-copy">
                      <strong>{label}</strong>
                    </span>
                    <div className="forms-theme-color-card-control">
                      <div
                        className="forms-color-swatch"
                        style={{ backgroundColor: draft.themeConfig.colors[field] }}
                      />
                      <div className="forms-theme-color-picker-shell">
                        <input
                          className="forms-color-picker-native"
                          type="color"
                          value={draft.themeConfig.colors[field]}
                          onChange={(event) => handleThemeChange('colors', field, event.target.value)}
                          disabled={!canEdit || saving}
                        />
                        <span className="forms-theme-color-value">
                          {String(draft.themeConfig.colors[field] || '').toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <label>
                Border-bredde
                <input
                  className="input"
                  type="number"
                  min="0"
                  max="6"
                  value={draft.themeConfig.borders.inputBorderWidth}
                  onChange={(event) => handleThemeChange('borders', 'inputBorderWidth', Number(event.target.value || 0))}
                  disabled={!canEdit || saving}
                />
              </label>
              <label>
                Border-radius
                <input
                  className="input"
                  type="number"
                  min="0"
                  max="32"
                  value={draft.themeConfig.borders.inputBorderRadius}
                  onChange={(event) => handleThemeChange('borders', 'inputBorderRadius', Number(event.target.value || 0))}
                  disabled={!canEdit || saving}
                />
              </label>
              <label>
                Feltavstand
                <input
                  className="input"
                  type="number"
                  min="4"
                  max="32"
                  value={draft.themeConfig.spacing.fieldGap}
                  onChange={(event) => handleThemeChange('spacing', 'fieldGap', Number(event.target.value || 0))}
                  disabled={!canEdit || saving}
                />
              </label>
              <label>
                Inner-padding
                <input
                  className="input"
                  type="number"
                  min="8"
                  max="40"
                  value={draft.themeConfig.spacing.formPadding}
                  onChange={(event) => handleThemeChange('spacing', 'formPadding', Number(event.target.value || 0))}
                  disabled={!canEdit || saving}
                />
              </label>
              <label className="forms-span-all">
                Knappetekst
                <input
                  className="input"
                  type="text"
                  value={draft.themeConfig.buttonText}
                  onChange={(event) => handleTopThemeChange('buttonText', event.target.value)}
                  disabled={!canEdit || saving}
                  placeholder="Send inn"
                />
              </label>
            </div>
          </div>
        ) : null}
      </section>

      <section className={`forms-advanced-card${openAdvancedSections.install ? ' is-open' : ''}`}>
        <button
          type="button"
          className="forms-advanced-toggle"
          onClick={() => toggleAdvancedSection('install')}
          aria-expanded={openAdvancedSections.install}
        >
          <span>
            <strong>Installering</strong>
            <small>Skjemaet rendres inline der script-taggen plasseres på nettsiden.</small>
          </span>
          <span>{openAdvancedSections.install ? 'Skjul' : 'Vis'}</span>
        </button>
        {openAdvancedSections.install ? (
          <div className="forms-advanced-body">
            <div className="forms-install-box">
              <code>{draft.externalId ? DashboardConfig.getFormEmbedSnippet(draft.externalId) : '<script src="https://chat.kichat.no/form-embed.js" form-id="din-form-id"></script>'}</code>
            </div>
          </div>
        ) : null}
      </section>

      {draft.validation?.is_valid === false ? (
        <div className="forms-validation-box">
          <strong>Skjemaet må repareres</strong>
          <p>{draft.validation.errors?.[0] || 'Skjemaet refererer til felter som ikke lenger finnes.'}</p>
        </div>
      ) : null}

      <div className="forms-editor-actions">
        <button type="button" className="button button-secondary" onClick={closeEditor} disabled={saving}>
          {mode === 'new' ? 'Avbryt' : 'Lukk'}
        </button>
        <button type="button" className="button button-primary" onClick={handleSave} disabled={!canEdit || saving}>
          {saving ? 'Lagrer…' : 'Lagre skjema'}
        </button>
      </div>
    </div>
  );

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page forms-page">
      <div className="page-header forms-page-header">
        <div>
          <h1>Skjema</h1>
          <p className="page-subtitle">
            Bygg innebygde skjemaer som skriver direkte til henvendelser med feltene fra en valgt bot og henvendelsestype.
          </p>
        </div>
        <div className="forms-page-header-actions">
          <button type="button" className="button button-secondary" onClick={loadPageData} disabled={loading || saving}>
            Oppdater
          </button>
          <button type="button" className="button button-primary" onClick={handleCreateNew} disabled={!canEdit}>
            Nytt skjema
          </button>
        </div>
      </div>

      <section className="card forms-page-filters">
        <label>
          Bot
          <select
            className="select"
            value={filters.botId}
            onChange={(event) => setFilters((prev) => ({ ...prev, botId: event.target.value }))}
          >
            <option value="all">Alle</option>
            {bots.map((bot) => (
              <option key={bot.id} value={bot.id}>
                {bot.display_title || bot.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Henvendelsestype
          <select
            className="select"
            value={filters.inquiryTypeId}
            onChange={(event) => setFilters((prev) => ({ ...prev, inquiryTypeId: event.target.value }))}
          >
            <option value="all">Alle</option>
            {Array.from(new Map(forms.map((form) => [String(form.inquiryTypeId), form])).values()).map((form) => (
              <option key={form.inquiryTypeId} value={form.inquiryTypeId}>
                {form.inquiryTypeName || `Type ${form.inquiryTypeId}`}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className="card forms-page-list">
        <div className="forms-page-list-head">
          <div>
            <h2>Skjemaer</h2>
            <p className="input-hint">Velg et skjema fra listen for å redigere det inline.</p>
          </div>
          <span className="forms-count-pill">{filteredForms.length}</span>
        </div>

        {activeEditorKey === 'new' ? (
          <article className="forms-row forms-row-expanded forms-row-draft">
            <div className="forms-row-draft-head">
              <div>
                <strong>Nytt skjema</strong>
                <p className="input-hint">Det nye skjemaet blir lagt til i listen så snart det er lagret.</p>
              </div>
            </div>
            {renderEditor('new')}
          </article>
        ) : null}

        {loading ? (
          <p className="input-hint">Laster skjemaer…</p>
        ) : filteredForms.length === 0 ? (
          <div className="forms-empty-state">
            <p>Ingen skjemaer matcher filtrene.</p>
            <p className="input-hint">Opprett et nytt skjema for å generere en egen `form-id` og embed-kode.</p>
          </div>
        ) : (
          <div className="forms-list-rows">
            {filteredForms.map((form) => {
              const isActive = activeEditorKey === form.externalId;
              const isInvalid = form.validation?.is_valid === false;
              return (
                <article
                  key={form.externalId}
                  className={`forms-row${isActive ? ' forms-row-expanded' : ''}${isInvalid ? ' is-invalid' : ''}`}
                >
                  <button
                    type="button"
                    className="forms-row-summary"
                    onClick={() => handleToggleExisting(form)}
                    disabled={saving}
                  >
                    <div className="forms-row-summary-main">
                      <div className="forms-row-summary-top">
                        <strong>{form.name}</strong>
                        <span className={`forms-status-pill is-${form.status}`}>
                          {form.status === 'archived' ? 'Arkivert' : 'Aktiv'}
                        </span>
                      </div>
                      <p>{form.botName || 'Ukjent bot'} · {form.inquiryTypeName || 'Ukjent henvendelsestype'}</p>
                      <small>{form.externalId}</small>
                    </div>
                    <span className="forms-row-summary-state">{isActive ? 'Lukk' : 'Åpne'}</span>
                  </button>

                  {isInvalid ? (
                    <div className="forms-list-card-error">
                      {form.validation?.errors?.[0] || 'Skjemaet må repareres før det kan brukes.'}
                    </div>
                  ) : null}

                  {isActive ? renderEditor('existing') : null}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
